import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Carte, Chargement, MessageErreur } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import {
  LIBELLES_OBJET_REFUS,
  LIBELLES_STATUT_AMENAGEMENT,
  useCreerAmenagement,
  useCreerRefus,
  useDeciderAmenagement,
  useFriseEquipe,
  useSuivreRefus,
  type Amenagement,
  type ObjetRefus,
  type RefusExprime,
  type StatutAmenagement,
} from '../../lib/useFrise';
import { FriseEvenements } from './ApprenantTimeline';

const FILTRES = ['Toutes', 'Accompagnement', 'Handicap', 'Aménagement', 'Refus', 'Dossier', 'Alerte'];

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function aujourdhui(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(new Date());
}

function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

function messageErreur(erreur: unknown): string | null {
  return erreur instanceof ErreurApi ? erreur.message : null;
}

function Champ({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
    </label>
  );
}

function FormulaireAmenagement({ apprenantId, onFermer }: { apprenantId: string; onFermer: () => void }): JSX.Element {
  const creer = useCreerAmenagement(apprenantId);
  const [demandeLe, setDemandeLe] = useState(aujourdhui());
  const [amenagement, setAmenagement] = useState('');
  const [commentaire, setCommentaire] = useState('');

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-codialis-sky/40 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_1fr]">
        <Champ libelle="Demandé le">
          <input type="date" value={demandeLe} onChange={(e) => setDemandeLe(e.target.value)} className={CLASSE_CHAMP} />
        </Champ>
        <Champ libelle="Aménagement demandé">
          <input
            value={amenagement}
            onChange={(e) => setAmenagement(e.target.value)}
            maxLength={300}
            placeholder="Tiers-temps, salle à part, secrétaire…"
            className={CLASSE_CHAMP}
          />
        </Champ>
      </div>
      <Champ libelle="Commentaire (facultatif)">
        <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={1000} className={CLASSE_CHAMP} />
      </Champ>
      {messageErreur(creer.error) && <MessageErreur message={messageErreur(creer.error) ?? ''} />}
      <div className="flex flex-wrap gap-2">
        <BoutonPrincipal
          onClick={() =>
            creer.mutate(
              { demandeLe, amenagement, ...(commentaire.trim() ? { commentaire } : {}) },
              { onSuccess: onFermer },
            )
          }
          disabled={creer.isPending || !demandeLe || amenagement.trim().length < 2}
        >
          Enregistrer la demande
        </BoutonPrincipal>
        <BoutonSecondaire onClick={onFermer} disabled={creer.isPending}>
          Annuler
        </BoutonSecondaire>
      </div>
    </div>
  );
}

function FormulaireRefus({ apprenantId, onFermer }: { apprenantId: string; onFermer: () => void }): JSX.Element {
  const creer = useCreerRefus(apprenantId);
  const [exprimeLe, setExprimeLe] = useState(aujourdhui());
  const [objet, setObjet] = useState<ObjetRefus>('ENTREPRISE');
  const [precision, setPrecision] = useState('');
  const [motif, setMotif] = useState('');

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-codialis-sky/40 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Champ libelle="Exprimé le">
          <input type="date" value={exprimeLe} onChange={(e) => setExprimeLe(e.target.value)} className={CLASSE_CHAMP} />
        </Champ>
        <Champ libelle="Ce qui est refusé">
          <select value={objet} onChange={(e) => setObjet(e.target.value as ObjetRefus)} className={CLASSE_CHAMP}>
            {Object.entries(LIBELLES_OBJET_REFUS).map(([valeur, libelle]) => (
              <option key={valeur} value={valeur}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ libelle="Précision (facultative)">
          <input
            value={precision}
            onChange={(e) => setPrecision(e.target.value)}
            maxLength={200}
            placeholder="Nom de l’entreprise, de l’atelier…"
            className={CLASSE_CHAMP}
          />
        </Champ>
      </div>
      <Champ libelle="Motif donné par l’apprenant">
        <input value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={1000} className={CLASSE_CHAMP} />
      </Champ>
      {messageErreur(creer.error) && <MessageErreur message={messageErreur(creer.error) ?? ''} />}
      <div className="flex flex-wrap gap-2">
        <BoutonPrincipal
          onClick={() =>
            creer.mutate(
              { exprimeLe, objet, motif, ...(precision.trim() ? { precision } : {}) },
              { onSuccess: onFermer },
            )
          }
          disabled={creer.isPending || !exprimeLe || motif.trim().length < 2}
        >
          Consigner le refus
        </BoutonPrincipal>
        <BoutonSecondaire onClick={onFermer} disabled={creer.isPending}>
          Annuler
        </BoutonSecondaire>
      </div>
    </div>
  );
}

function LigneAmenagement({ apprenantId, ligne }: { apprenantId: string; ligne: Amenagement }): JSX.Element {
  const decider = useDeciderAmenagement(apprenantId);
  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-codialis-navy">{ligne.amenagement}</div>
        <div className="text-[11px] text-codialis-gray">
          Demandé le {dateCourte(ligne.demandeLe)}
          {ligne.decideLe ? ` · décidé le ${dateCourte(ligne.decideLe)}` : ''}
          {ligne.commentaire ? ` · ${ligne.commentaire}` : ''}
        </div>
      </div>
      <label className="shrink-0">
        <span className="sr-only">Décision sur « {ligne.amenagement} »</span>
        <select
          value={ligne.statut}
          onChange={(e) => decider.mutate({ id: ligne.id, statut: e.target.value as StatutAmenagement })}
          disabled={decider.isPending}
          className="min-h-[40px] rounded-lg border border-gray-200 bg-white px-2 text-xs text-codialis-navy"
        >
          {Object.entries(LIBELLES_STATUT_AMENAGEMENT).map(([valeur, libelle]) => (
            <option key={valeur} value={valeur}>
              {libelle}
            </option>
          ))}
        </select>
      </label>
      {messageErreur(decider.error) && (
        <p role="alert" className="w-full text-[11px] text-red-700">
          {messageErreur(decider.error)}
        </p>
      )}
    </li>
  );
}

function LigneRefus({ apprenantId, ligne }: { apprenantId: string; ligne: RefusExprime }): JSX.Element {
  const suivre = useSuivreRefus(apprenantId);
  const [suivi, setSuivi] = useState('');
  return (
    <li className="space-y-2 py-2">
      <div className="text-sm font-semibold text-codialis-navy">
        {LIBELLES_OBJET_REFUS[ligne.objet]}
        {ligne.precision ? ` · ${ligne.precision}` : ''}
      </div>
      <div className="text-[11px] text-codialis-gray">
        Exprimé le {dateCourte(ligne.exprimeLe)} · {ligne.motif}
      </div>
      {ligne.suivi ? (
        <div className="text-[11px] text-codialis-navy">
          Suivi{ligne.suiviLe ? ` du ${dateCourte(ligne.suiviLe)}` : ''} : {ligne.suivi}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <label className="min-w-0 flex-1 basis-56">
            <span className="sr-only">Suivi donné au refus</span>
            <input
              value={suivi}
              onChange={(e) => setSuivi(e.target.value)}
              maxLength={1000}
              placeholder="Suivi donné à ce refus"
              className={CLASSE_CHAMP}
            />
          </label>
          <BoutonSecondaire
            onClick={() => suivre.mutate({ id: ligne.id, suivi })}
            disabled={suivre.isPending || suivi.trim().length < 2}
          >
            Consigner le suivi
          </BoutonSecondaire>
        </div>
      )}
      {messageErreur(suivre.error) && (
        <p role="alert" className="text-[11px] text-red-700">
          {messageErreur(suivre.error)}
        </p>
      )}
    </li>
  );
}

/**
 * Frise du dossier lue par l'équipe (CDC §8.3) : accompagnement, handicap,
 * aménagements d'examen, refus exprimés par l'apprenant et leur suivi,
 * alertes. Les pôles pédagogique et administratif y tiennent les aménagements
 * et les refus ; les autres la lisent.
 */
export function CarteFriseDossier({ apprenantId }: { apprenantId: string }): JSX.Element {
  const { data, isLoading, error } = useFriseEquipe(apprenantId);
  const [filtre, setFiltre] = useState('Toutes');
  const [saisie, setSaisie] = useState<'amenagement' | 'refus' | null>(null);

  return (
    <Carte titre="Frise du dossier" sansBordure>
      <div className="space-y-5 p-4 sm:p-6">
        {isLoading && <Chargement libelle="Chargement de la frise…" />}
        {error instanceof ErreurApi && <MessageErreur message={error.message} />}

        {data && (
          <>
            {data.peutSaisir && saisie === null && (
              <div className="flex flex-wrap gap-2">
                <BoutonSecondaire onClick={() => setSaisie('amenagement')}>
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                  Aménagement d’examen
                </BoutonSecondaire>
                <BoutonSecondaire onClick={() => setSaisie('refus')}>
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                  Refus exprimé
                </BoutonSecondaire>
              </div>
            )}
            {saisie === 'amenagement' && (
              <FormulaireAmenagement apprenantId={apprenantId} onFermer={() => setSaisie(null)} />
            )}
            {saisie === 'refus' && <FormulaireRefus apprenantId={apprenantId} onFermer={() => setSaisie(null)} />}

            {data.peutSaisir && data.amenagements.length > 0 && (
              <section>
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Aménagements d’examen
                </h4>
                <ul className="divide-y divide-gray-100">
                  {data.amenagements.map((ligne) => (
                    <LigneAmenagement key={ligne.id} apprenantId={apprenantId} ligne={ligne} />
                  ))}
                </ul>
              </section>
            )}
            {data.peutSaisir && data.refus.some((ligne) => !ligne.suivi) && (
              <section>
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Refus en attente de suivi
                </h4>
                <ul className="divide-y divide-gray-100">
                  {data.refus
                    .filter((ligne) => !ligne.suivi)
                    .map((ligne) => (
                      <LigneRefus key={ligne.id} apprenantId={apprenantId} ligne={ligne} />
                    ))}
                </ul>
              </section>
            )}

            <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer la frise">
              {FILTRES.map((libelle) => (
                <button
                  key={libelle}
                  type="button"
                  aria-pressed={filtre === libelle}
                  onClick={() => setFiltre(libelle)}
                  className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
                    filtre === libelle
                      ? 'bg-codialis-navy text-white'
                      : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
                  }`}
                >
                  {libelle}
                </button>
              ))}
            </div>
            <FriseEvenements
              evenements={data.evenements}
              filtre={filtre}
              vide="Aucun événement n’est encore enregistré sur ce dossier."
            />
          </>
        )}
      </div>
    </Carte>
  );
}
